import React, { useEffect, useState } from "react";
import { Breadcrumbs } from "../AdminLayout";
import { homepageApi } from "./api";
import { formatErr } from "../api";
import { toast, Toaster } from "sonner";
import { Save, Send, RotateCcw, History } from "lucide-react";

export default function HomepageEditor() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({ hero_title: "", hero_subtitle: "", hero_image_url: "", hero_primary_cta_label: "", hero_primary_cta_href: "", hero_secondary_cta_label: "", hero_secondary_cta_href: "", scheduled_publish_at: "", notes: "", blocks: [] });
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(false);

  const reload = () => {
    homepageApi.get().then(h => {
      setData(h);
      if (h.draft) setDraft({ ...draft, ...h.draft });
      else if (h.current) setDraft({ ...draft, ...h.current, scheduled_publish_at: "" });
    }).catch(e => toast.error(formatErr(e.response?.data?.detail)));
    homepageApi.versions().then(setVersions).catch(() => {});
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const saveDraft = async () => {
    setBusy(true);
    try { await homepageApi.saveDraft(draft); toast.success("Draft saved"); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const publish = async () => {
    if (!window.confirm("Publish this draft to the live homepage?")) return;
    setBusy(true);
    try { await homepageApi.publish(); toast.success("Published"); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const rollback = async (id) => {
    if (!window.confirm("Rollback the live homepage to this version?")) return;
    try { await homepageApi.rollback(id); toast.success("Rolled back"); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const inputCls = "w-full border border-line px-3 py-2 text-sm";
  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="homepage-editor">
      <Toaster position="top-right" richColors/>
      <Breadcrumbs items={[{ label: "Site" }, { label: "Homepage" }]}/>
      <div className="flex items-center justify-between mt-4 mb-6">
        <div className="font-serif text-3xl text-navy">Homepage editor</div>
        <div className="flex items-center gap-2">
          <button data-testid="btn-save-hp-draft" onClick={saveDraft} disabled={busy} className="border border-line hover:border-navy text-navy text-sm px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60"><Save size={13}/> Save draft</button>
          <button data-testid="btn-publish-hp" onClick={publish} disabled={busy} className="bg-navy hover:bg-navy-hover text-white text-sm px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60"><Send size={13}/> Publish</button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8 space-y-4">
          <div className="bg-white border border-line p-5 space-y-3">
            <div className="mono text-[10px] uppercase tracking-widest text-prestige">Hero</div>
            <input data-testid="hp-hero-title" value={draft.hero_title || ""} onChange={e => set("hero_title", e.target.value)} placeholder="Hero title" className={inputCls}/>
            <textarea data-testid="hp-hero-subtitle" rows={2} value={draft.hero_subtitle || ""} onChange={e => set("hero_subtitle", e.target.value)} placeholder="Subtitle" className={inputCls}/>
            <input data-testid="hp-hero-image" value={draft.hero_image_url || ""} onChange={e => set("hero_image_url", e.target.value)} placeholder="Hero image URL" className={inputCls}/>
            <div className="grid grid-cols-2 gap-2">
              <input value={draft.hero_primary_cta_label || ""} onChange={e => set("hero_primary_cta_label", e.target.value)} placeholder="Primary CTA label" className={inputCls}/>
              <input value={draft.hero_primary_cta_href || ""} onChange={e => set("hero_primary_cta_href", e.target.value)} placeholder="Primary CTA URL" className={inputCls}/>
              <input value={draft.hero_secondary_cta_label || ""} onChange={e => set("hero_secondary_cta_label", e.target.value)} placeholder="Secondary CTA label" className={inputCls}/>
              <input value={draft.hero_secondary_cta_href || ""} onChange={e => set("hero_secondary_cta_href", e.target.value)} placeholder="Secondary CTA URL" className={inputCls}/>
            </div>
          </div>
          <div className="bg-white border border-line p-5 space-y-3">
            <div className="mono text-[10px] uppercase tracking-widest text-prestige">Scheduling</div>
            <input data-testid="hp-schedule" type="datetime-local" value={(draft.scheduled_publish_at || "").slice(0, 16)} onChange={e => set("scheduled_publish_at", e.target.value)} className={inputCls}/>
            <textarea data-testid="hp-notes" rows={2} value={draft.notes || ""} onChange={e => set("notes", e.target.value)} placeholder="Editor notes (not shown publicly)" className={inputCls}/>
          </div>
          <div className="bg-white border border-line p-5 space-y-3">
            <div className="mono text-[10px] uppercase tracking-widest text-prestige">Blocks (JSON)</div>
            <div className="mono text-[10px] text-stone2">Edit block structure as JSON. Live rendering configurable in the public app.</div>
            <textarea data-testid="hp-blocks" rows={10} value={JSON.stringify(draft.blocks ?? [], null, 2)}
              onChange={e => { try { set("blocks", JSON.parse(e.target.value)); } catch { /* keep partial input in local state */ } }}
              className={`${inputCls} font-mono text-xs`}/>
          </div>
        </div>

        <aside className="col-span-12 lg:col-span-4 space-y-4">
          <div className="bg-white border border-line p-5">
            <div className="mono text-[10px] uppercase tracking-widest text-prestige">Live version</div>
            {data?.current ? (
              <div className="mt-2 text-xs text-navy">
                <div className="font-medium truncate">{data.current.hero_title || "(untitled)"}</div>
                <div className="mono text-[10px] text-stone2 mt-1">Published {data.current.published_at}</div>
              </div>
            ) : <div className="mt-2 text-xs text-stone2">Nothing published yet.</div>}
          </div>
          <div className="bg-white border border-line p-5">
            <div className="mono text-[10px] uppercase tracking-widest text-prestige mb-3 flex items-center gap-2"><History size={11}/> Version history</div>
            {versions.length === 0 ? (
              <div className="text-xs text-stone2">No prior versions.</div>
            ) : (
              <ul className="space-y-2 max-h-80 overflow-y-auto">
                {versions.map(v => (
                  <li key={v.id} data-testid={`hp-version-${v.id}`} className="flex items-center justify-between gap-2 border border-line px-2 py-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-navy truncate">{v.payload?.hero_title || "(no title)"}</div>
                      <div className="mono text-[9px] uppercase tracking-widest text-stone2 truncate">{v.committed_at}</div>
                    </div>
                    <button onClick={() => rollback(v.id)} data-testid={`btn-rollback-${v.id}`} className="mono text-[10px] uppercase tracking-widest text-navy hover:text-terracotta inline-flex items-center gap-1"><RotateCcw size={11}/> Rollback</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
